/**
 * File: ExecutionPlanPanel.tsx
 * Description: Detailed execution plan component showing agent tool calls
 * Author: GitHub Copilot
 * Created: 2026-02-10
 * Last Modified: 2026-02-16
 */

import { ArrowPathIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { ExecutionStep } from '../types/execution';

const EXECUTION_PLAN_LABEL = 'Execution Plan';

interface ExecutionPlanPanelProps {
  title?: string;
  executionPlan: ExecutionStep[] | string[] | null;
  isLoading?: boolean;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

export const ExecutionPlanPanel: React.FC<ExecutionPlanPanelProps> = ({
  title = EXECUTION_PLAN_LABEL,
  executionPlan,
  isLoading = false,
  isExpanded = false,
  onToggleExpanded,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  
  const expanded = (onToggleExpanded !== undefined) ? isExpanded : internalExpanded;
  const handleToggle = onToggleExpanded ?? (() => setInternalExpanded(!internalExpanded));

  const renderStepContent = (step: ExecutionStep) => {
    const hasStandardProps = (step.agent_name ?? step.output ?? step.reasoning ?? step.tool_calls) !== undefined;
    
    if (hasStandardProps) {
      return (
        <>
          {step.agent_name && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">
                {step.agent_name}
              </span>
            </div>
          )}
          
          {step.output && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-text-onSurface-strong">Output:</p>
              <p className="text-xs text-text-onSurface-subtle whitespace-pre-wrap break-words">{step.output}</p>
            </div>
          )}
          
          {step.reasoning && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-text-onSurface-strong">Reasoning:</p>
              <p className="text-xs text-text-onSurface-subtle whitespace-pre-wrap break-words">{step.reasoning}</p>
            </div>
          )}
          
          {step.tool_calls && step.tool_calls.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-text-onSurface-strong">Tool Calls:</p>
              <div className="space-y-2">
                {step.tool_calls.map((tool, toolIndex) => (
                  <div key={`${tool.tool_name ?? 'tool'}-${toolIndex}-${typeof tool.tool_input === 'string' ? tool.tool_input.substring(0, 10) : ''}`} className="border-l-2 border-blue-200 pl-3 space-y-1 min-w-0">
                    <p className="text-xs font-medium text-blue-700 break-words">{tool.tool_name ?? 'Unknown Tool'}</p>
                    {tool.tool_input && tool.tool_input !== '{}' && (
                      <div className="space-y-0.5">
                        <p className="text-xs font-medium text-text-onSurface-subtle">Input:</p>
                        <pre className="text-xs text-text-onSurface-subtle bg-background-base p-2 rounded overflow-x-auto whitespace-pre-wrap break-words max-w-full">
                          {typeof tool.tool_input === 'string' ? tool.tool_input : JSON.stringify(tool.tool_input, null, 2)}
                        </pre>
                      </div>
                    )}
                    {tool.tool_output && (
                      <div className="space-y-0.5">
                        <p className="text-xs font-medium text-text-onSurface-subtle">Output:</p>
                        <pre className="text-xs text-text-onSurface-subtle bg-background-base p-2 rounded overflow-x-auto whitespace-pre-wrap break-words max-w-full max-h-40">
                          {typeof tool.tool_output === 'string' ? tool.tool_output : JSON.stringify(tool.tool_output, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      );
    }

    // Fallback for simple objects
    return (
      <>
        {step.description && (
          <p className="text-xs text-text-onSurface-strong font-medium">{step.description}</p>
        )}
        {step.details && (
          <p className="text-xs text-text-onSurface-subtle mt-1">{step.details}</p>
        )}
        {step.tool && (
          <p className="text-xs text-text-onSurface-subtle">Tool: {step.tool}</p>
        )}
        {step.query && (
          <p className="text-xs text-text-onSurface-subtle">Query: {step.query}</p>
        )}
      </>
    );
  };

  return (
    <div className="flex-shrink-0">
      <button
        onClick={handleToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-background-section transition-colors"
      >
        <div className="flex items-center gap-2">
          <h5 className="text-sm font-medium text-text-onSurface-strong">{title}</h5>
          {isLoading && (
            <ArrowPathIcon className="w-4 h-4 text-blue-600 animate-spin" />
          )}
        </div>
        <svg
          className={`w-5 h-5 text-text-onSurface-subtle transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          {(() => {
            if (isLoading) {
              return (
                <div className="text-xs text-text-onSurface-subtle">
                  Loading execution plan...
                </div>
              );
            }
            
            if (executionPlan && executionPlan.length > 0) {
              return (
                <div className="space-y-4">
                  {executionPlan.map((step, index) => {
                    const stepKey = typeof step === 'string' 
                      ? `step-${index}-${step.substring(0, 20)}` 
                      : `step-${index}-${step.agent_name ?? ''}-${step.output?.substring(0, 10) ?? ''}`;
                    const stepContent = typeof step === 'string' 
                      ? <p className="text-xs text-text-onSurface-strong">{step}</p>
                      : renderStepContent(step);
                    
                    return (
                      <div key={stepKey} className="border border-border-subtle rounded-lg p-3 bg-background-section">
                        <div className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-medium text-xs">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0 space-y-2">
                            {stepContent}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }
            
            return (
              <p className="text-xs text-text-onSurface-subtle">
                No execution plan available
              </p>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default ExecutionPlanPanel;